// (2026-06-09) Maestro de Ordenes de Compra — PAGINA LISTA.
//
// Reconstruido como MAESTRO identico al de Proformas (Ventas):
//   - Lista como pagina (esta) con boton "Nueva OC" -> /compras/ordenes/create.
//   - Formulario como PAGINA aparte (OrdenCompraForm.js) en /create y /edit.
//   - Detalle en MODAL (no se usa modal para crear/editar).
//
// CONSERVA toda la logica de negocio y llamadas API del modulo de Compras
// (mismos endpoints de ../../controllers/api_compras, mismos estados, mismas
// acciones de fila aprobar/enviar/recibir/anular y "convertir a Compra").
//
// Reusa los primitivos de ./ui (Card, PageHeader, Table/Th/Td, Modal, IconBtn,
// Badge, BtnGhost, SectionTitle) y respeta _DESIGN_GUIDE.md. NADA de @mui.

import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthContext } from "../../context/AuthContext";
import AdminLayout from "../../layouts/AdminLayout";
import { encryptText } from "../../controllers/api";
import {
  getOrdenesCompra, getOrdenCompra, approveOC, sendOC, receiveOC, cancelOC, ocToPurchase,
} from "../../controllers/api_compras";
import { monedaCodigo, fmt, handleApiError } from "./comprasShared";
import {
  Card, PageHeader, Table, Th, Td, Modal, IconBtn, BtnGhost, SectionTitle,
} from "./ui";

// Mapa estatus -> clases de badge (mantiene la semantica de color original).
const ESTATUS_BADGE = {
  borrador: "bg-slate-100 text-slate-700",
  aprobada: "bg-twilight-indigo-100 text-twilight-indigo-800",
  enviada: "bg-twilight-indigo-100 text-twilight-indigo-800",
  recibida_parcial: "bg-amber-100 text-amber-800",
  recibida_total: "bg-emerald-400 text-white",
  cerrada: "bg-emerald-400 text-white",
  anulada: "bg-red-400 text-white",
};
const badgeClass = (s) => ESTATUS_BADGE[s] || "bg-slate-100 text-slate-700";

export default function OrdenesCompraList() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [detalle, setDetalle] = useState(null); // OC completa para el modal de detalle

  const cargar = async () => {
    try { setRows(await getOrdenesCompra()); } catch (e) { handleApiError(e); }
  };
  useEffect(() => { cargar(); }, []);

  // --- Navegacion (igual que el maestro de Proformas: encryptText + query ?id=) ---
  const redirectToCreate = () => navigate("/compras/ordenes/create");
  const redirectToEdit = (id) => {
    const hash = encryptText(id.toString());
    navigate(`/compras/ordenes/edit?id=${encodeURIComponent(hash)}`);
  };

  // --- Detalle (modal de lectura) ---
  const verDetalle = async (r) => {
    try { setDetalle(await getOrdenCompra(r.id)); }
    catch (e) { handleApiError(e); }
  };

  // --- Acciones de estado (CONSERVADAS de la pagina actual de Compras) ---
  const accion = async (fn, r, okMsg) => {
    try { await fn(r.id); toast.success(okMsg); cargar(); } catch (e) { handleApiError(e); }
  };
  const recibir = async (r) => {
    if (!window.confirm("¿Marcar como recibida totalmente?")) return;
    try { await receiveOC(r.id, { recibir_todo: true }); toast.success("Recepción registrada."); cargar(); }
    catch (e) { handleApiError(e); }
  };
  const aCompra = async (r) => {
    // (2026-06-10) Convención del sistema: los textos capturados van en
    // MAYÚSCULAS y son OBLIGATORIOS. Cancelar el prompt (null) aborta sin
    // mensaje, como siempre; vacío tras trim => toast canónico "- mensaje"
    // (patrón validate() de ProveedoresForm) y NO se llama a ocToPurchase.
    const nfRaw = window.prompt("Nº de factura del proveedor:"); if (nfRaw === null) return;
    const ncRaw = window.prompt("Nº de control del proveedor:"); if (ncRaw === null) return;
    const nf = String(nfRaw).toUpperCase().trim();
    const nc = String(ncRaw).toUpperCase().trim();
    const errorToast = [];
    if (!nf) errorToast.push("- El nº de factura del proveedor es obligatorio");
    if (!nc) errorToast.push("- El nº de control del proveedor es obligatorio");
    if (errorToast.length > 0) {
      toast.error(
        <div>
          {errorToast.map((item, i) => (
            <span key={i} className="text-start">{item}<br /></span>
          ))}
        </div>
      );
      return;
    }
    try {
      await ocToPurchase(r.id, { numero_factura_proveedor: nf, numero_control_proveedor: nc });
      toast.success("Compra creada desde la OC."); cargar();
    } catch (e) { handleApiError(e); }
  };

  // Solo borrador es editable (mismo criterio del backend: una OC ya aprobada
  // no se reedita por la cabecera; el resto solo cambia de estado).
  const editable = (s) => s === "borrador";

  return (
    <AdminLayout nombreUsuario={user?.nombre || "Usuario"}>
      <div className="mt-8 px-4 md:px-8">
        <Card>
          <PageHeader
            title="Lista de Órdenes de Compra"
            icon="fa-file-invoice-dollar"
            actions={
              <button
                className="bg-twilight-indigo-600 hover:bg-twilight-indigo-500 text-white font-bold py-2 px-4 rounded inline-flex items-center gap-2"
                onClick={redirectToCreate}
              >
                <i className="fas fa-plus" aria-hidden="true" />
                Nueva OC
              </button>
            }
          />

          <Table>
            <thead>
              <tr>
                <Th>Nº OC</Th>
                <Th>Proveedor</Th>
                <Th>Fecha</Th>
                <Th className="text-right">Base</Th>
                <Th className="text-right">I.V.A.</Th>
                <Th className="text-right">Total</Th>
                <Th className="text-center">Mon.</Th>
                <Th className="text-center">Estatus</Th>
                <Th className="text-center">Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <Td className="text-center text-blueGray-400 italic" colSpan={9}>
                    Sin órdenes de compra.
                  </Td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-blueGray-50 transition-colors">
                  <Td>{r.numero_oc}</Td>
                  <Td>{r.proveedor_nombre || r.proveedor?.nombre}</Td>
                  <Td>{(r.fecha_emision || "").slice(0, 10)}</Td>
                  <Td className="text-right">{fmt(r.total_base)}</Td>
                  <Td className="text-right">{fmt(r.total_impuestos)}</Td>
                  <Td className="text-right font-semibold">{fmt(r.total_neto)}</Td>
                  <Td className="text-center">{monedaCodigo(r.moneda_id)}</Td>
                  <Td className="text-center">
                    <span className={`inline-block px-2 py-1 text-xs font-bold rounded whitespace-nowrap ${badgeClass(r.estatus)}`}>
                      {r.estatus}
                    </span>
                  </Td>
                  <Td className="text-center">
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "0.25rem", whiteSpace: "nowrap" }}>
                      <IconBtn icon="fa-expand" title="Ver" color="text-gray-700" onClick={() => verDetalle(r)} />
                      {editable(r.estatus) && (
                        <IconBtn icon="fa-pen-to-square" title="Editar" color="text-blue-600 hover:text-blue-800" onClick={() => redirectToEdit(r.id)} />
                      )}
                      {r.estatus === "borrador" && (
                        <IconBtn icon="fa-check" title="Aprobar" color="text-twilight-indigo-600" onClick={() => accion(approveOC, r, "OC aprobada.")} />
                      )}
                      {r.estatus === "aprobada" && (
                        <IconBtn icon="fa-paper-plane" title="Enviar" color="text-twilight-indigo-600" onClick={() => accion(sendOC, r, "OC enviada.")} />
                      )}
                      {["enviada", "aprobada", "recibida_parcial"].includes(r.estatus) && (
                        <IconBtn icon="fa-box-open" title="Recibir" color="text-amber-600" onClick={() => recibir(r)} />
                      )}
                      {["aprobada", "enviada", "recibida_parcial", "recibida_total"].includes(r.estatus) && (
                        <IconBtn icon="fa-arrow-right-to-bracket" title="Convertir a Compra" color="text-emerald-600" onClick={() => aCompra(r)} />
                      )}
                      {!["anulada", "cerrada"].includes(r.estatus) && (
                        <IconBtn icon="fa-rectangle-xmark" title="Anular" color="text-red-600" onClick={() => accion(cancelOC, r, "OC anulada.")} />
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>

        {/* MODAL: Detalle de la OC (solo lectura) */}
        <Modal
          open={!!detalle}
          onClose={() => setDetalle(null)}
          title={`Orden de compra ${detalle?.numero_oc || ""}`}
          icon="fa-file-invoice-dollar"
        >
          {detalle && (
            <>
              <div className="flex flex-wrap -mx-4">
                <DetalleItem label="Proveedor" icon="fa-truck-field" full>
                  {detalle.proveedor?.nombre || detalle.proveedor_nombre || "—"}
                  {detalle.proveedor?.rif ? ` (${detalle.proveedor.rif})` : ""}
                </DetalleItem>
                <DetalleItem label="Fecha de emisión" icon="fa-calendar-day">
                  {(detalle.fecha_emision || "").slice(0, 10) || "—"}
                </DetalleItem>
                <DetalleItem label="Moneda" icon="fa-coins">
                  {monedaCodigo(detalle.moneda_id) || detalle.moneda?.codigo || "—"}
                </DetalleItem>
                <DetalleItem label="Estatus" icon="fa-flag">
                  <span className={`inline-block px-2 py-1 text-xs font-bold rounded ${badgeClass(detalle.estatus)}`}>
                    {detalle.estatus}
                  </span>
                </DetalleItem>
                <DetalleItem label="Condición de pago" icon="fa-handshake">
                  {detalle.condicion_pago || "—"}
                </DetalleItem>
                {detalle.observaciones && (
                  <DetalleItem label="Observaciones" icon="fa-note-sticky" full>
                    {detalle.observaciones}
                  </DetalleItem>
                )}
              </div>

              <SectionTitle icon="fa-list-ul">Ítems</SectionTitle>
              <Table>
                <thead>
                  <tr>
                    <Th>Descripción</Th>
                    <Th className="text-right">Cant.</Th>
                    <Th className="text-right">Recibida</Th>
                    <Th className="text-right">Precio unit.</Th>
                    <Th className="text-center">IVA</Th>
                    <Th className="text-right">Total</Th>
                  </tr>
                </thead>
                <tbody>
                  {(detalle.items || []).length === 0 && (
                    <tr>
                      <Td className="text-center text-blueGray-400 italic" colSpan={6}>Sin ítems.</Td>
                    </tr>
                  )}
                  {(detalle.items || []).map((it, i) => {
                    const alic = Number(it.iva_categoria?.tasa_porcentaje || 0);
                    const base = Number(it.cantidad || 0) * Number(it.precio_unitario || 0);
                    const tot = base * (1 + alic / 100);
                    return (
                      <tr key={it.id || i}>
                        <Td>{it.descripcion}</Td>
                        <Td className="text-right">{fmt(it.cantidad)}</Td>
                        <Td className="text-right">{fmt(it.cantidad_recibida)}</Td>
                        <Td className="text-right">{fmt(it.precio_unitario)}</Td>
                        <Td className="text-center">{it.iva_categoria ? `${alic}%` : "—"}</Td>
                        <Td className="text-right">{fmt(tot)}</Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              <div className="flex justify-end mt-4 px-4">
                <div className="w-full lg:w-5/12">
                  <ResumenLinea label="Base imponible" value={`${fmt(detalle.total_base)} ${monedaCodigo(detalle.moneda_id)}`} />
                  <ResumenLinea label="I.V.A." value={`${fmt(detalle.total_impuestos)} ${monedaCodigo(detalle.moneda_id)}`} />
                  <ResumenLinea label="Total" value={`${fmt(detalle.total_neto)} ${monedaCodigo(detalle.moneda_id)}`} strong />
                </div>
              </div>
            </>
          )}
        </Modal>

        <ToastContainer position="bottom-right" autoClose={3000} />
      </div>
    </AdminLayout>
  );
}

/* Helpers visuales del modal de detalle (estilo guia: tarjeta slate + label). */
function DetalleItem({ label, icon, children, full }) {
  return (
    <div className={full ? "w-full px-4 mb-3" : "w-full sm:w-1/2 lg:w-1/3 px-4 mb-3"}>
      <div className="bg-slate-50/70 rounded-xl border border-slate-200/70 p-4 h-full">
        <h3 className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] font-semibold text-ink-500 mb-2">
          {icon && <i className={`fas ${icon} text-sirius-500 text-[12px]`} aria-hidden="true" />}
          {label}
        </h3>
        <div className="text-sm font-medium text-ink-900 break-words leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function ResumenLinea({ label, value, strong }) {
  return (
    <div className="flex w-full justify-between items-center mb-1 px-4">
      <label className={`text-blueGray-600 ${strong ? "font-bold text-lg" : "font-bold"}`}>{label}</label>
      <label className={`text-blueGray-600 text-right ${strong ? "font-bold text-lg" : "font-bold"}`}>{value}</label>
    </div>
  );
}
